import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import {
  ConversationMetrics,
  EmotionAnalysis,
  FillerStats,
  ProsodyAnalysis,
  SpeechSegment,
} from '../../../services/api.service';
import { buildSpeakerLabelIndex } from '../utils/voice-analytics.utils';
import FillerStatsCard from './filler-stats-card.component';
import InteractionMatrixCard from './interaction-matrix-card.component';
import DominanceBalanceCard from './dominance-balance-card.component';
import EmotionsCard from './emotions-card.component';
import ProsodyMarkersCard from './prosody-markers-card.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type SpeakerTimeline = {
  name: string;
  color: string;
};

type Props = {
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  conversationMetrics: ConversationMetrics | null;
  fillerStats: FillerStats | null;
  emotionAnalysis: EmotionAnalysis | null;
  prosodyAnalysis: ProsodyAnalysis | null;
  recomputingAnalytics: boolean;
  onRecomputeAnalytics: () => void;
};

/** Задача F1: карточки новой голосовой аналитики (H1 filler_stats, B1 conversation_metrics,
 *  B2 emotion/prosody) — рендерятся в аналитической панели после ленты и бенчмарк-блока.
 *  Каждая карточка сама решает, показываться ли (скрывается, если по ней нет данных). */
const VoiceAnalyticsCards: React.FC<Props> = ({
  segments,
  speakerTimelines,
  conversationMetrics,
  fillerStats,
  emotionAnalysis,
  prosodyAnalysis,
  recomputingAnalytics,
  onRecomputeAnalytics,
}) => {
  const { t } = useTranslation();

  const labelIndex = useMemo(
    () => buildSpeakerLabelIndex(segments, speakerTimelines),
    [segments, speakerTimelines],
  );

  return (
    <>
      {!conversationMetrics && (
        <div className={cn('voice-recompute-banner')}>
          <p className={cn('voice-recompute-text')}>{t('voice-analytics.recompute-banner')}</p>
          <button
            type="button"
            className={cn('analytics-refresh-btn')}
            onClick={onRecomputeAnalytics}
            disabled={recomputingAnalytics}
          >
            {recomputingAnalytics ? t('voice-analytics.recompute-loading') : t('voice-analytics.recompute-button')}
          </button>
        </div>
      )}

      <FillerStatsCard fillerStats={fillerStats} labelIndex={labelIndex} />

      {conversationMetrics && (
        <>
          <InteractionMatrixCard
            interactionMatrix={conversationMetrics.interaction_matrix}
            interruptions={conversationMetrics.interruptions}
            labelIndex={labelIndex}
          />
          <DominanceBalanceCard
            dominance={conversationMetrics.dominance}
            balanceDynamics={conversationMetrics.balance_dynamics}
            labelIndex={labelIndex}
          />
        </>
      )}

      <EmotionsCard emotionAnalysis={emotionAnalysis} labelIndex={labelIndex} />
      <ProsodyMarkersCard prosodyAnalysis={prosodyAnalysis} labelIndex={labelIndex} />
    </>
  );
};

export default VoiceAnalyticsCards;
